import pMap from 'p-map';
import { useCallback } from 'react';
import { type FileWithPath } from 'react-dropzone';
import { useAnalytics } from '@/services/analytics';
import { uploadAsset } from '@/services/db/asset/upload-asset';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { type Asset } from '@/services/db/asset/entity';

import { useRefetchAssets } from '../browser/hooks/use-refetch-assets';
import { MAX_FILE_SIZE, MAX_FILE_SIZE_MB, MAX_IMAGE_DIMENSION, getImageDimensions } from '../utils';
import { $assetUploadState } from './asset-upload-modal';
import {
  type AllowedFileCategory,
  type AssetProcessedFile,
  type RejectionReason,
  IMAGE_MIME_TYPES,
  isTypeAllowed,
  isTypeSupported,
} from './utils';

export function useAssetUpload() {
  const { track } = useAnalytics();
  const workspaceId = requireWorkspaceId();
  const refetchAssets = useRefetchAssets();

  const processPendingFiles = useCallback(
    async (files: FileWithPath[], allowedFileCategories?: AllowedFileCategory[]) => {
      const acceptedFiles: AssetProcessedFile[] = [];
      const rejectedFiles: AssetProcessedFile[] = [];

      for (const file of files) {
        const fileProperties: AssetProcessedFile = {
          ...file,
          path: file.path ?? file.name,
          name: file.name,
          type: file.type,
          size: file.size,
          state: 'PENDING',
        };

        if (!isTypeAllowed(allowedFileCategories, file)) {
          const extension = file.name.split('.').pop();
          rejectedFiles.push({
            ...fileProperties,
            reason: `Design Studio does not support uploading ${extension} files ${
              isTypeSupported(file) ? 'here' : ''
            }`,
            state: 'FAILED',
          });
          continue;
        }

        const rejection = await validateFileConstraints(file);

        if (rejection != null) {
          const isImage = IMAGE_MIME_TYPES.has(file.type);
          const isSizeOnly = rejection.reasons.has('file-size') && rejection.reasons.size === 1;

          rejectedFiles.push({
            ...fileProperties,
            _file: isImage && isSizeOnly ? file : undefined,
            reason: rejection.messages.join(' '),
            rejectionReasons: rejection.reasons,
            state: 'FAILED',
          });
          continue;
        }

        acceptedFiles.push({
          ...fileProperties,
          _file: file,
        });
      }

      return [acceptedFiles, rejectedFiles] as const;
    },
    [],
  );

  const upload = useCallback(
    async ({
      allowedFileCategories = ['image', 'doc'],
      files,
    }: {
      allowedFileCategories?: AllowedFileCategory[];
      files: FileWithPath[];
    }) => {
      const [acceptedFiles, rejectedFiles] = await processPendingFiles(files, allowedFileCategories);

      const state = $assetUploadState.get();
      const parentFolderId = state.parentFolderId;

      $assetUploadState.set({
        ...state,
        acceptedFiles,
        rejectedFiles,
      });

      if (acceptedFiles.length > 0) {
        track('assetLibrary', 'uploadStarted', {
          parent_folder_id: parentFolderId,
          file_count: acceptedFiles.length,
        });
      }

      const result = await pMap(
        acceptedFiles,
        async (currentFile) => {
          updateFile(currentFile.path, { state: 'UPLOADING' });

          try {
            if (currentFile._file == null) {
              throw new Error('Cannot upload file: missing file data');
            }

            // uploadAsset returns a parsed Asset directly (throws on error)
            const asset = await uploadAsset({
              workspaceId,
              file: currentFile._file,
              parentFolderId: parentFolderId ?? undefined,
            });

            updateFile(currentFile.path, { state: 'SUCCESS' });

            const currentState = $assetUploadState.get();
            $assetUploadState.set({
              ...currentState,
              uploadedAssets: [...(currentState.uploadedAssets ?? []), asset],
            });

            track('asset', 'uploaded', {
              id: asset.id,
              path: `${asset.path}`,
            });

            track('assetLibrary', 'uploadCompleted', {
              asset_id: asset.id,
              file_type: currentFile.name.split('.').pop() ?? '',
              parent_folder_id: parentFolderId,
            });

            return asset;
          } catch (error) {
            updateFile(currentFile.path, {
              state: 'FAILED',
              reason: (error as Error).message,
            });
          }
        },
        { concurrency: 5, stopOnError: false },
      );

      const uploadedAssets = result.filter(Boolean) as Asset[];

      $assetUploadState.set({
        ...$assetUploadState.get(),
        uploadedAssets,
      });

      $assetUploadState.get().onUpload?.(uploadedAssets);
      void refetchAssets();
    },
    [processPendingFiles, workspaceId, refetchAssets, track],
  );

  return upload;
}

function updateFile(filePath: string, delta: Pick<AssetProcessedFile, 'state' | 'reason'>) {
  $assetUploadState.set({
    ...$assetUploadState.get(),
    acceptedFiles: $assetUploadState
      .get()
      .acceptedFiles?.map((file) => (file.path === filePath ? { ...file, ...delta } : file)),
  });
}

async function validateFileConstraints(
  file: FileWithPath,
): Promise<{ messages: string[]; reasons: Set<RejectionReason> } | null> {
  const messages: string[] = [];
  const reasons = new Set<RejectionReason>();
  const isImage = IMAGE_MIME_TYPES.has(file.type);

  if (file.size > MAX_FILE_SIZE) {
    messages.push(`Files must be under ${MAX_FILE_SIZE_MB} MB.`);
    reasons.add('file-size');
  }

  if (isImage) {
    try {
      const objectUrl = URL.createObjectURL(file);
      try {
        const { width, height } = await getImageDimensions(objectUrl);
        if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
          messages.push(`Image dimensions exceed the ${MAX_IMAGE_DIMENSION}px limit.`);
          reasons.add('image-dimensions');
        }
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    } catch {
      // If we can't read dimensions, let the server validate
    }
  }

  return reasons.size > 0 ? { messages, reasons } : null;
}
